import type { AdLoader } from '@/modules/adv/ad-loader'
import { AdService } from '@/modules/adv/ad-service'
import { OutOfPageAd } from '@/modules/adv/out-of-page-ad'
import { type AdHandlerConfig, AdvAction, type OutOfPageAdType } from '@/modules/adv/types'
import type { GameEvent, GameMessageAction, SmartAdsConfig, SmartAdsRule } from '@/types'
import type { LoggerEvent } from '@/types/logger.types'

import EventBus from '../../event-bus'
import {
    type AdBlockerHandler,
    type AdDisabledHandler,
    type AdResponsePayload,
    type AdStateChangedAction,
    type AdStateChangedEventPayload,
    type BannerPositionSizeRaw,
    type GameAdHandlerConfig,
    type ShortGameEvent,
} from './game-ad-handler.types'

const DEFAULT_INTERVAL_MIDROLL_MS = 180_000 // 3 minutes
const DEFAULT_MIN_TIME_BETWEEN_MIDROLL_AND_INTERSTITIAL_MS = 60_000 // 1 minute
const DEFAULT_MIN_TIME_BEFORE_MIDROLL_MS = 60_000 // 1 minute
type SmartAdsType = 'ai_smart_ads' | 'ai_smart_ads-cloud-save'

function findSmartAdsRule(config: SmartAdsConfig, requestType: SmartAdsType) {
    return config.ads?.rules?.findLast(({ data }) => data?.request?.includes(requestType))
}

export class GameAdHandler {
    private isShowingFullscreenAd = false

    get isFullscreenAdOnScreen() {
        return this.isShowingFullscreenAd
    }

    private banners = new Map<OutOfPageAdType, OutOfPageAd>()

    private originalMessages = new Map<string, ShortGameEvent>()

    private lastShownTimestamps = new Map<OutOfPageAdType, number>()

    private lastUsedSmartAdsIntervals = new Map<SmartAdsType, number>([
        ['ai_smart_ads', 0],
        ['ai_smart_ads-cloud-save', 0],
    ])

    private lastAdStatusAction = new Map<OutOfPageAdType, AdStateChangedAction>()

    private initTime: number

    private midrollTimer: number | null = null
    private adBlockerInProgress: boolean = false

    private adStateChangedEventId: string | null = null

    private midrollsRule: SmartAdsRule | undefined
    private cloudSaveMidrollsRule: SmartAdsRule | undefined

    eventBus = new EventBus<GameAdHandlerConfig>()

    constructor(
        private adService: AdService,
        private logEvent: (event: LoggerEvent) => void,
        private options: {
            adHandlerConfig?: Partial<AdHandlerConfig>
            smartAdsConfig?: SmartAdsConfig
            adBlockerHandler?: AdBlockerHandler
            adDisabledHandler?: AdDisabledHandler
            adBlockDetected?: () => boolean
            loader?: AdLoader
            getAdRootElementId?: () => string | undefined
        } = {},
    ) {
        this.initTime = Date.now()
        if (options.smartAdsConfig) {
            this.midrollsRule = findSmartAdsRule(options.smartAdsConfig, 'ai_smart_ads')
            this.cloudSaveMidrollsRule = findSmartAdsRule(options.smartAdsConfig, 'ai_smart_ads-cloud-save')
        }
    }

    get activeSmartAdsRules(): SmartAdsType[] {
        const rules: [SmartAdsType, SmartAdsRule | undefined][] = [
            ['ai_smart_ads', this.midrollsRule],
            ['ai_smart_ads-cloud-save', this.cloudSaveMidrollsRule],
        ]
        return rules.flatMap(([type, rule]) => (rule ? [type] : []))
    }

    refreshInitTime() {
        this.initTime = Date.now()
    }

    private handleAdDisabled(adAction: GameMessageAction, messageId: string): boolean {
        const result = this.options.adDisabledHandler?.(adAction)
        if (!result) {
            return false
        }
        if (adAction === AdvAction.showRewarded) {
            this.fakeBannerOpening(messageId, 'rewarded', result)
        } else if (adAction === AdvAction.showInterstitial) {
            this.fakeBannerOpening(messageId, 'interstitial', result)
        } else {
            this.originalMessages.delete(messageId)
        }
        return true
    }

    setAdvancedBanners(banners: BannerPositionSizeRaw[], messageId: string) {
        if (!messageId) {
            return
        }
        // set banners to ExportPage
        this.eventBus.dispatch('setAdvancedBanners', banners)
        this.originalMessages.delete(messageId)
    }

    stopMidrollTimer() {
        if (this.midrollTimer) {
            clearTimeout(this.midrollTimer)
            this.midrollTimer = null
        }
    }

    startMidrollTimer(offset = 0) {
        if (!this.midrollsRule) {
            return
        }
        const MIDROLL_INTERVAL = this.getNextMidrollIntervalMs(this.midrollsRule, 'ai_smart_ads')

        this.stopMidrollTimer()
        const delay = Math.max(MIDROLL_INTERVAL - offset, 0)
        this.midrollTimer = window.setTimeout(() => {
            this.attemptMidrollAd()
        }, delay)
    }

    private canShowMidroll(rule: SmartAdsRule, minTimeBetweenMidrolls?: number) {
        const skipIfAdShownWithinSec = rule?.data?.skipIfAdShownWithinSec ?? 0
        const minTimeBeforeMidroll = skipIfAdShownWithinSec * 1000 || DEFAULT_MIN_TIME_BEFORE_MIDROLL_MS

        const now = Date.now()
        const lastCall = Math.max(
            this.lastShownTimestamps.get('interstitial') || 0,
            this.lastShownTimestamps.get('rewarded') || 0,
        )
        if (
            this.isShowingFullscreenAd ||
            now - lastCall < minTimeBeforeMidroll ||
            document.visibilityState === 'hidden' ||
            this.options.adDisabledHandler?.()
        ) {
            return false
        }

        if (minTimeBetweenMidrolls !== undefined) {
            const lastMidrollCall = this.lastShownTimestamps.get('interstitial_midroll') || 0
            return now - lastMidrollCall >= minTimeBetweenMidrolls
        }

        return true
    }

    private async attemptMidrollAd(attempt = 1) {
        const MIDROLL_RETRY_DELAY = 10_000 // 10 seconds
        if (!this.midrollsRule) {
            return
        }

        const messageId = `MIDROLL_MESSAGE_ID_${Date.now()}`
        this.originalMessages.set(messageId, {})

        if (this.canShowMidroll(this.midrollsRule)) {
            console.info(`ATTEMPTING_MIDROLL_AD, ATTEMPT №${attempt}`)
            return await this.showAd('interstitial', messageId)
        }

        if (attempt < 2) {
            this.midrollTimer = window.setTimeout(() => {
                this.attemptMidrollAd(attempt + 1)
            }, MIDROLL_RETRY_DELAY)
        } else {
            this.startMidrollTimer(MIDROLL_RETRY_DELAY)
        }

        this.originalMessages.delete(messageId)
    }

    async attemptMidrollOnCloudSave() {
        if (!this.cloudSaveMidrollsRule) {
            return
        }
        const MIN_TIME_BETWEEN_MIDROLLS = this.getNextMidrollIntervalMs(
            this.cloudSaveMidrollsRule,
            'ai_smart_ads-cloud-save',
        )

        const messageId = `MIDROLL_MESSAGE_ID_${Date.now()}`
        this.originalMessages.set(messageId, {})

        if (this.canShowMidroll(this.cloudSaveMidrollsRule, MIN_TIME_BETWEEN_MIDROLLS)) {
            console.info(`ATTEMPTING_MIDROLL_AD_CLOUD_SAVE`)
            return await this.showAd('interstitial', messageId)
        }

        this.originalMessages.delete(messageId)
    }

    async handleMessage(event: GameEvent) {
        this.originalMessages.set(event.data.id, event)

        // Subscription is a control message, not an ad request — it must register even when ads are
        // disabled (ad-free subscription), otherwise fake ads can't report their state back to the
        // game and it never receives adStateChanged statuses.
        if (event.data.action === AdvAction.subscribeToAdStateChanges) {
            this.adStateChangedEventId = event.data.id
            return
        }

        if (this.handleAdDisabled(event.data.action, event.data.id)) {
            return
        }
        switch (event.data.action) {
            case AdvAction.setBannerAdvSettings:
                this.eventBus.dispatch('adMessage', {
                    action: AdvAction.setBannerAdvSettings,
                    originalMessageEvent: event as GameEvent,
                    payload: {},
                    type: 'adv',
                })
                break
            case AdvAction.showAdvancedBanners: {
                this.setAdvancedBanners((event.data.payload?.banners || []) as BannerPositionSizeRaw[], event.data.id)
                break
            }
            case AdvAction.hideAdvancedBanners:
                this.setAdvancedBanners([], event.data.id)
                break
            case AdvAction.preloadInterstitial:
                this.logEvent({ event: 'custom_event', eventName: 'tmp_ad_preoad_used', dimension1: event.data.action })
                await this.preloadAd('interstitial', event.data.id)
                break
            case AdvAction.preloadRewarded:
                this.logEvent({ event: 'custom_event', eventName: 'tmp_ad_preoad_used', dimension1: event.data.action })
                await this.preloadAd('rewarded', event.data.id)
                break
            case AdvAction.showInterstitial:
                await this.showAd('interstitial', event.data.id)
                break
            case AdvAction.showRewarded:
                await this.adBlockOfferProcessing('rewarded', event.data.id)
                break
            case AdvAction.checkAdBlock:
                this.respondToCheckAdBlock(event.data.id)
                break
            default:
                this.eventBus.dispatch('adMessage', {
                    originalMessageEvent: event as GameEvent,
                    payload: {
                        error: `Unknown message action "${event.data.action}"`,
                    },
                    type: 'error',
                })
                this.originalMessages.delete(event.data.id)
        }
    }

    async showPreroll() {
        const PREROLL_MESSAGE_ID = 'PREROLL_MESSAGE_ID'
        this.originalMessages.set(PREROLL_MESSAGE_ID, {})
        await this.showAd('interstitial_preroll', PREROLL_MESSAGE_ID)
    }

    private validateAdDisplayConditions(placement: OutOfPageAdType, messageId: string) {
        if (document.visibilityState === 'hidden') {
            this.notifyGameAboutAdState(
                {
                    status: 'error',
                    error: 'Page is not visible - skip ad',
                },
                messageId,
                placement,
            )
            return 'PAGE_IS_NOT_VISIBLE'
        }
        const GENERAL_TIMEOUT_BETWEEN_ADS = 5_000 // 5 seconds between ads of the same type
        const MIN_TIME_BETWEEN_PREROLL_AND_INTERSTITIAL = 60_000 // 60 seconds between preroll and interstitial
        const MIN_TIME_BETWEEN_INIT_AND_INTERSTITIAL = 10_000 // 10 seconds between game init and interstitial
        const rulesForSkipAfter = this.midrollsRule ?? this.cloudSaveMidrollsRule
        const skipIfAdShownWithinSecAfter = rulesForSkipAfter?.data?.skipIfAdShownWithinSecAfter ?? 0
        const minTimeBetweenMidrollAndInterstitialMs =
            skipIfAdShownWithinSecAfter * 1000 || DEFAULT_MIN_TIME_BETWEEN_MIDROLL_AND_INTERSTITIAL_MS

        const now = Date.now()

        const lastPrerollCall = this.lastShownTimestamps.get('interstitial_preroll')
        const lastMidrollCall = this.lastShownTimestamps.get('interstitial_midroll')
        const lastCall = this.lastShownTimestamps.get(placement)
        const lastAdStatusAction = this.lastAdStatusAction.get('interstitial_preroll')
        const minTimeBetweenAds = this.options.adHandlerConfig?.[placement]?.timeout || GENERAL_TIMEOUT_BETWEEN_ADS
        const initialDelayAd = this.options.adHandlerConfig?.[placement]?.initialDelayInMs || 0

        const shouldFakeInterstitial =
            placement === 'interstitial' &&
            ((lastPrerollCall && now - lastPrerollCall < MIN_TIME_BETWEEN_PREROLL_AND_INTERSTITIAL) ||
                (now - this.initTime < MIN_TIME_BETWEEN_INIT_AND_INTERSTITIAL && lastAdStatusAction !== 'empty') ||
                now - this.initTime < initialDelayAd)

        if (shouldFakeInterstitial) {
            this.fakeBannerOpening(messageId, placement)
            return 'EARLY_INTERSTITIAL'
        }
        const isMidroll = messageId.startsWith('MIDROLL_MESSAGE_ID')

        if (
            placement === 'interstitial' &&
            ((!isMidroll && lastMidrollCall && now - lastMidrollCall < minTimeBetweenMidrollAndInterstitialMs) ||
                (isMidroll && lastCall && now - lastCall < minTimeBetweenMidrollAndInterstitialMs))
        ) {
            this.fakeBannerOpening(messageId, placement)
            return `TOO_MANY_${minTimeBetweenMidrollAndInterstitialMs / 1000}s_REQUESTS`
        }

        if (lastCall && now - lastCall < minTimeBetweenAds) {
            this.notifyGameAboutAdState(
                {
                    status: 'error',
                    error: 'Too many requests for this ad type',
                },
                messageId,
                placement,
            )

            return `TOO_MANY_${minTimeBetweenAds / 1000}s_REQUESTS`
        }
        if (this.isShowingFullscreenAd || this.adBlockerInProgress) {
            this.notifyGameAboutAdState(
                {
                    status: 'error',
                    error: 'Another ad is already open',
                },
                messageId,
                placement,
            )
            return 'AD_ALREADY_OPEN'
        }

        return 'ok'
    }

    private async showAd(placement: OutOfPageAdType, messageId: string) {
        const validationResult = this.validateAdDisplayConditions(placement, messageId)
        if (validationResult !== 'ok') {
            this.logEvent({
                event: 'custom_event',
                eventName: 'in_game_ad_not_requested',
                adPlacement: placement,
                dimension1: validationResult,
            })
            return
        }
        // set flag as soon as possible to prevent call ad multiple times while current is in progress
        this.isShowingFullscreenAd = true

        this.notifyExportPageAboutAdState('start', placement)

        const banner = this.banners.get(placement) || (await this.preloadAd(placement, messageId))

        if (!banner) {
            this.endFullscreenAd(placement, 'empty')
            return
        }
        this.listenToRenderedBannerEvents(banner, placement, messageId)
        banner.addEventListener('empty', () => {
            this.notifyGameAboutAdState({ status: 'close' }, messageId, placement)
            this.endFullscreenAd(placement, 'empty')
        })

        try {
            banner.show()
        } catch {
            this.endFullscreenAd(placement, 'empty')
        }

        this.banners.delete(placement)
    }

    private async preloadAd(placement: OutOfPageAdType, messageId: string): Promise<OutOfPageAd | null> {
        try {
            if (!this.originalMessages.get(messageId)) {
                throw new Error('Message not found')
            }

            this.notifyGameAboutAdState({ status: 'start' }, messageId, placement)

            const banner = this.adService.requestOutOfPageAd(placement, {
                loader: this.options.loader,
                adRootElementId: this.options.getAdRootElementId?.(),
            })

            const { promise, resolve, reject } = Promise.withResolvers<OutOfPageAd>()

            banner.addEventListener('empty', () => {
                this.notifyGameAboutAdState({ status: 'empty' }, messageId, placement)
                this.notifyExportPageAboutAdState('empty', placement)
                reject(new Error('No ads available'))
            })
            banner.addEventListener('ready', () => {
                this.notifyGameAboutAdState({ status: 'done' }, messageId, placement)
                this.notifyExportPageAboutAdState('done', placement)
                this.banners.set(placement, banner)
                resolve(banner)
            })

            return await promise
        } catch {
            this.notifyGameAboutAdState({ status: 'error', error: 'Cannot preload the ad' }, messageId, placement)
            this.notifyExportPageAboutAdState('empty', placement)
            this.originalMessages.delete(messageId)
            return null
        }
    }

    private listenToRenderedBannerEvents(banner: OutOfPageAd, placement: OutOfPageAdType, messageId: string) {
        const message = this.originalMessages.get(messageId)
        if (!message) {
            return
        }

        banner.addEventListener('rewarded', () => {
            this.notifyGameAboutAdState({ status: 'rewarded' }, messageId, placement)
        })

        banner.addEventListener('clicked', () => {
            this.notifyGameAboutAdState({ status: 'clicked' }, messageId, placement)
        })

        banner.addEventListener('timer', ({ detail }) => {
            this.notifyExportPageAboutAdState('timer', placement, {
                secondsLeft: detail?.secondsLeft,
            })
        })

        banner.addEventListener('pod_counter', (event) => {
            this.notifyExportPageAboutAdState('pod_counter', placement, {
                adPosition: event.detail?.adPosition,
                totalAds: event.detail?.totalAds,
            })
        })

        banner.addEventListener('closed', () => {
            const now = Date.now()
            this.lastShownTimestamps.set(placement, now)

            this.notifyGameAboutAdState({ status: 'close' }, messageId, placement)
            this.originalMessages.delete(messageId)

            this.endFullscreenAd(placement, 'closed')

            if (placement === 'interstitial_preroll') {
                this.logEvent({
                    event: 'custom_event',
                    eventName: 'preroll_end',
                    adUnit: banner.adUnit,
                    instreamDuration: banner.instreamDuration,
                    watchedDuration: banner.watchedDuration,
                })
            }

            if (messageId.startsWith('MIDROLL_MESSAGE_ID')) {
                this.lastShownTimestamps.set('interstitial_midroll', now)
                this.startMidrollTimer()
            }
        })

        banner.addEventListener('rendered', () => {
            this.notifyGameAboutAdState({ status: 'open' }, messageId, placement)
        })

        banner.addEventListener('viewable', () => {
            this.notifyGameAboutAdState({ status: 'show' }, messageId, placement)
            this.notifyExportPageAboutAdState('opened', placement)

            if (messageId.startsWith('MIDROLL_MESSAGE_ID')) {
                this.logEvent({
                    event: 'custom_event',
                    eventName: 'ad_show_midroll',
                    adPlacement: placement,
                })
            }
        })
    }

    private notifyGameAboutAdState(payload: AdResponsePayload, messageId: string, placement: OutOfPageAdType) {
        const message = this.originalMessages.get(messageId)

        if (payload.status === 'rewarded') {
            this.notifyExportPageAboutAdState('rewarded', placement)
        }

        if (message?.data) {
            this.eventBus.dispatch('adMessage', {
                action: message.data.action,
                type: 'adv',
                originalMessageEvent: message,
                payload,
            })
        }

        const event = this.adStateChangedEventId && this.originalMessages.get(this.adStateChangedEventId)
        if (event) {
            this.eventBus.dispatch('adMessage', {
                action: AdvAction.adStateChanged,
                type: 'adv',
                originalMessageEvent: event,
                payload: {
                    status: payload.status,
                    adType: placement,
                },
            })
        }
    }

    private endFullscreenAd(placement: OutOfPageAdType, action: 'closed' | 'empty') {
        this.isShowingFullscreenAd = false
        this.notifyExportPageAboutAdState(action, placement)
    }

    private notifyExportPageAboutAdState(
        action: AdStateChangedAction,
        placement: OutOfPageAdType,
        message: Omit<AdStateChangedEventPayload, 'action' | 'adType'> = {},
    ) {
        this.lastAdStatusAction.set(placement, action)
        this.eventBus.dispatch('adStateChanged', {
            action,
            adType: placement,
            ...message,
        })
    }

    private async fakeBannerOpening(
        messageId: string,
        placement: OutOfPageAdType,
        waitPromise?: Promise<{ reason: string }>,
    ) {
        this.notifyGameAboutAdState({ status: 'start' }, messageId, placement)

        if (waitPromise) {
            const reasonResult = await waitPromise
            this.logEvent({
                event: 'custom_event',
                eventName: 'ad_skipped',
                adPlacement: placement,
                dimension1: reasonResult.reason,
            })
        }

        setTimeout(() => this.notifyGameAboutAdState({ status: 'done' }, messageId, placement), 200)
        setTimeout(() => this.notifyGameAboutAdState({ status: 'open' }, messageId, placement), 400)
        setTimeout(() => this.notifyGameAboutAdState({ status: 'show' }, messageId, placement), 500)
        if (placement === 'rewarded') {
            setTimeout(() => this.notifyGameAboutAdState({ status: 'rewarded' }, messageId, placement), 500)
        }
        setTimeout(() => {
            this.notifyGameAboutAdState({ status: 'close' }, messageId, placement)
            this.originalMessages.delete(messageId)
        }, 1_000)
    }

    private async adBlockOfferProcessing(placement: OutOfPageAdType, messageId: string) {
        if (!this.options.adBlockerHandler) {
            await this.showAd(placement, messageId)
            return
        }
        if (this.validateAdDisplayConditions(placement, messageId) !== 'ok') {
            return
        }
        try {
            this.adBlockerInProgress = true
            const { message } = await this.options.adBlockerHandler(placement)
            this.adBlockerInProgress = false
            if (message === 'fakeShowAd') {
                this.fakeBannerOpening(messageId, placement)
            } else {
                await this.showAd(placement, messageId)
            }
        } catch (err) {
            this.adBlockerInProgress = false
            console.error('AdBlocker handler error', err)
            await this.showAd(placement, messageId)
        }
    }

    private respondToCheckAdBlock(messageId: string) {
        const message = this.originalMessages.get(messageId)
        if (message?.data) {
            this.eventBus.dispatch('adMessage', {
                action: message.data.action,
                type: 'adv',
                originalMessageEvent: message,
                payload: { adBlockDetected: !!this.options.adBlockDetected?.() },
            })
        }
        this.originalMessages.delete(messageId)
    }

    private getNextMidrollIntervalMs(rule: SmartAdsRule, type: SmartAdsType) {
        const { intervalScheduleSec = [], scheduleMode } = rule?.data || {}
        if (intervalScheduleSec.length === 0) {
            return DEFAULT_INTERVAL_MIDROLL_MS
        }

        const index = this.lastUsedSmartAdsIntervals.get(type) || 0
        let intervalSec = intervalScheduleSec[Math.min(index, intervalScheduleSec.length - 1)]

        if (scheduleMode === 'repeatLast') {
            if (index < intervalScheduleSec.length - 1) {
                this.lastUsedSmartAdsIntervals.set(type, index + 1)
            }
        } else {
            // For any non-repeatLast mode, loop through the provided schedule.
            intervalSec = intervalScheduleSec[index % intervalScheduleSec.length]
            this.lastUsedSmartAdsIntervals.set(type, (index + 1) % intervalScheduleSec.length)
        }

        return (intervalSec ?? 0) * 1000 || DEFAULT_INTERVAL_MIDROLL_MS
    }
}
